import { Transition } from '@headlessui/react'
import { forwardRef, useEffect, useState } from 'react'
import { Icon } from '../Icon'
import { Spinner as CCUISpinner } from '../Spinner'
import { tm } from '../tm'
import { Tooltip } from '../Tooltip'
import type { IconKeys } from '../Icon'
import type { TooltipPlacement } from '../Tooltip'
import type { Locales } from '@captaincause/types'
import type {
  CSSProperties,
  FocusEventHandler,
  KeyboardEventHandler,
  MouseEventHandler,
  ReactNode,
} from 'react'
import './Button.css'

const VARIANTS = {
  primary: {
    base: 'px-4 py-3 rounded-full text-lg h-fit sm:h-13 text-white bg-black opacity-100 hover:bg-black/80 focus-visible:bg-black/80 active:bg-black/60',
    small:
      'px-4 py-2 rounded-full text-base md:text-sm lg:text-base h-fit sm:h-10 text-white bg-black opacity-100 hover:bg-black/80 focus-visible:bg-black/80 active:bg-black/60',
    large: 'px-12 py-3 text-xl',
    disabled:
      'cursor-not-allowed bg-medium-gray/30 text-dark-gray hover:bg-medium-gray/30 focus-visible:bg-medium-gray/30 active:bg-medium-gray/30',
  },
  secondary: {
    base: 'px-4 py-3 rounded-full text-lg h-fit sm:h-13 text-black border border-inside bg-transparent opacity-100 hover:bg-light-beige focus-visible:bg-light-beige hover:border-opacity-80 focus-visible:border-opacity-80 hover:text-black/80 focus-visible:text-black/80 active:border-opacity-60 active:text-black/60 active:text-black/60',
    small:
      'px-4 py-2 rounded-full text-base md:text-sm lg:text-base h-fit sm:h-10 text-black border border-inside bg-transparent opacity-100 hover:bg-light-beige focus-visible:bg-light-beige hover:border-opacity-80 focus-visible:border-opacity-80 hover:text-black/80 focus-visible:text-black/80 active:border-opacity-60 active:text-black/60 active:text-black/60',
    large: 'px-12 py-3 text-xl',
    disabled:
      'cursor-not-allowed border-gray text-dark-gray hover:bg-white focus-visible:bg-white active:bg-white hover:border-gray focus-visible:border-gray active:border-gray hover:text-dark-gray focus-visible:text-dark-gray active:text-dark-gray hover:border-opacity-100 focus-visible:border-opacity-100 active:border-opacity-100',
  },
  tertiary: {
    base: 'p-1 font-medium rounded-lg text-base text-black underline underline-offset-4 opacity-100 hover:bg-light-beige focus-visible:bg-light-beige hover:text-black/80 focus-visible:text-black/80 active:text-black/60',
    small:
      'p-1 rounded-lg text-xs text-black underline opacity-100 hover:bg-light-beige focus-visible:bg-light-beige hover:text-black/80 focus-visible:text-black/80 active:text-black/60',
    large: 'p-2 text-lg',
    disabled:
      'cursor-not-allowed text-dark-gray hover:bg-unset focus-visible:bg-unset hover:text-dark-gray focus-visible:text-dark-gray active:text-dark-gray',
  },
  naked: {
    base: 'p-1 rounded-lg opacity-100 hover:bg-light-beige focus-visible:bg-light-beige active:bg-light-beige/60',
    small: '',
    large: '',
    disabled:
      'cursor-not-allowed bg-light-gray hover:bg-light-gray focus-visible:bg-light-gray [&>*]:opacity-50',
  },
  raw: {
    base: 'font-normal text-base opacity-100',
    small: '',
    large: '',
    disabled: 'cursor-not-allowed',
  },
}

interface CommonProps {
  ariaLabel?: string
  'aria-controls'?: string
  'aria-expanded'?: boolean | 'true' | 'false'
  breakout?: boolean
  children?: ReactNode
  className?: string
  dataIndex?: number
  dataTestId?: string
  disabled?: boolean
  full?: boolean
  href?: string
  iconClassName?: string
  iconLeft?: IconKeys
  iconRight?: IconKeys
  id?: string
  inline?: boolean
  innerClassName?: string
  isProgress?: boolean
  large?: boolean
  locale?: Locales | false
  noWrap?: boolean
  onBlur?: FocusEventHandler<HTMLAnchorElement | HTMLButtonElement>
  onClick?: MouseEventHandler<HTMLAnchorElement | HTMLButtonElement>
  onFocus?: FocusEventHandler<HTMLAnchorElement | HTMLButtonElement>
  onMouseEnter?: MouseEventHandler<HTMLAnchorElement | HTMLButtonElement>
  onMouseLeave?: MouseEventHandler<HTMLAnchorElement | HTMLButtonElement>
  onKeyDown?: KeyboardEventHandler<HTMLAnchorElement | HTMLButtonElement>
  small?: boolean
  style?: CSSProperties
  submit?: boolean
  relative?: string
  tabIndex?: number
  targetBlank?: boolean
  targetSelf?: boolean
  tooltip?: string
  tooltipId?: string
  tooltipPlacement?: TooltipPlacement
  underline?: boolean
  variant?: keyof typeof VARIANTS
  // We currently support two Link component for client-side routing
  isNextLink?: boolean // see https://nextjs.org/docs/pages/api-reference/components/link#href-required
  isReactRouterLink?: boolean // see https://reactrouter.com/en/main/components/link
}

export type ClientSideLink = (props: any) => ReactNode

export type ButtonProps = CommonProps & { Link?: ClientSideLink }

const TARGET_BLANK = { target: '_blank', rel: 'noreferrer noopener' }

const noop = () => {}
function ButtonWithRef(
  {
    Link = undefined,
    ariaLabel = '',
    'aria-controls': ariaControls = undefined,
    'aria-expanded': ariaExpanded = undefined,
    breakout = false,
    children = null,
    className = '',
    dataIndex = undefined,
    dataTestId = undefined,
    disabled = false,
    full = false,
    href = '',
    iconClassName = '',
    iconLeft = undefined,
    iconRight = undefined,
    id = undefined,
    inline = false,
    innerClassName = '',
    isProgress = false,
    large = false,
    locale = undefined,
    noWrap = false,
    onBlur = noop,
    onClick = noop,
    onFocus = noop,
    onMouseEnter = noop,
    onMouseLeave = noop,
    onKeyDown = noop,
    small = false,
    style = undefined,
    submit = false,
    relative = 'route',
    tabIndex = undefined,
    targetSelf = false,
    targetBlank = false,
    tooltip = undefined,
    tooltipId = undefined,
    tooltipPlacement = 'bottom',
    underline = false,
    variant = 'primary',
    isNextLink = false,
    isReactRouterLink = false,
  }: ButtonProps,
  ref: any,
) {
  const [shouldShowSpinner, setShowSpinner] = useState<boolean>(false)
  const isLink = !!href
  const isExternalLink = isLink ? href.startsWith('http') : false

  const nextLinkOptions =
    isNextLink && isLink && !isExternalLink ? { locale } : {}
  const reactRouterLinkOptions =
    isReactRouterLink && isLink && !isExternalLink ? { to: href, relative } : {}

  const hrefOptions =
    // eslint-disable-next-line no-nested-ternary
    targetBlank
      ? TARGET_BLANK
      : isExternalLink && !targetSelf
      ? TARGET_BLANK
      : {}

  const commonProps = {
    id,
    ref,
    'aria-label': ariaLabel || undefined,
    'aria-controls': ariaControls || undefined,
    'aria-expanded': ariaExpanded || undefined,
    disabled: disabled || isProgress,
    tabIndex,
    'data-index': dataIndex ?? undefined,
    'data-testid': dataTestId,
    style,
    onMouseEnter,
    onMouseLeave,
    onFocus,
    onBlur,
    className: tm(
      'relative flex w-fit items-center justify-center font-medium transition-button duration-300 ease-out',
      VARIANTS[variant]?.base ?? VARIANTS.primary.base,
      small && (VARIANTS[variant]?.small ?? VARIANTS.primary.small),
      large && (VARIANTS[variant]?.large ?? VARIANTS.primary.large),
      (disabled || isProgress) &&
        (VARIANTS[variant]?.disabled ?? VARIANTS.primary.disabled),
      full && 'w-full',
      noWrap && 'whitespace-nowrap',
      breakout && 'breakout-link static',
      style && 'hover:opacity-70 focus-visible:opacity-70 active:opacity-70',
      iconLeft && 'pl-11',
      iconRight && 'pr-11',
      iconLeft && large && 'pl-16',
      iconRight && large && 'pr-16',
      iconLeft && variant === 'tertiary' && 'pl-6',
      iconRight && variant === 'tertiary' && 'pr-6',
      iconLeft && variant === 'naked' && 'pl-9 pr-2',
      iconRight && variant === 'naked' && 'pr-9 pl-2',
      underline && 'link-underline',
      inline && 'inline',
      className,
    ),
  }

  const linkProps = {
    href,
    ...hrefOptions,
    ...nextLinkOptions,
    ...reactRouterLinkOptions,
  }

  const tooltipProps = {
    id: `button-tooltip-${tooltipId}`,
    label: tooltip,
    disabled: !tooltip,
    placement: tooltipPlacement,
  }

  const Spinner = (
    <Transition
      show={isProgress}
      as="div"
      className="absolute left-1/2 -translate-x-1/2 transition-opacity duration-300 ease-out data-[closed]:opacity-0 data-[enter]:delay-300 data-[leave]:delay-0"
      unmount
    >
      <CCUISpinner />
    </Transition>
  )

  const IconLeft = iconLeft ? (
    <Icon
      icon={iconLeft}
      className={tm(
        'absolute left-3 opacity-100 duration-300 ease-out',
        shouldShowSpinner && '-translate-y-2 !opacity-0',
        variant === 'tertiary' && 'left-0',
        variant === 'naked' && 'right-2',
        iconClassName,
      )}
      size={variant === 'tertiary' ? 'small' : 'medium'}
    />
  ) : null

  const IconRight = iconRight ? (
    <Icon
      icon={iconRight}
      className={tm(
        'absolute right-3 opacity-100 duration-300 ease-out',
        shouldShowSpinner && '-translate-y-2 !opacity-0',
        variant === 'tertiary' && 'right-0',
        variant === 'naked' && 'right-2',
        iconClassName,
      )}
      size={variant === 'tertiary' ? 'small' : 'medium'}
    />
  ) : null

  const Children = (
    <div
      className={tm(
        'opacity-100 transition-transform delay-300 duration-300 ease-out',
        shouldShowSpinner && '-translate-y-2 !opacity-0 delay-200',
        inline && 'inline',
        innerClassName,
      )}
    >
      {children}
    </div>
  )

  useEffect(() => {
    setShowSpinner(isProgress)
  }, [isProgress])

  if (isLink && isExternalLink) {
    return (
      <Tooltip {...tooltipProps}>
        <a {...commonProps} {...linkProps}>
          {Spinner}
          {IconLeft}
          {Children}
          {IconRight}
        </a>
      </Tooltip>
    )
  }

  if (isLink && !isExternalLink) {
    if (!Link) {
      // eslint-disable-next-line no-console
      console.error(
        `Button - error: relative href should have a client-side Link component\nCheck for href "${href}"`,
      )

      return null
    }

    return (
      <Tooltip {...tooltipProps}>
        <Link {...commonProps} {...linkProps}>
          {Spinner}
          {IconLeft}
          {Children}
          {IconRight}
        </Link>
      </Tooltip>
    )
  }

  return (
    <Tooltip {...tooltipProps}>
      <button
        type={submit ? 'submit' : 'button'}
        {...commonProps}
        onClick={onClick}
        onKeyDown={onKeyDown}
      >
        {Spinner}
        {IconLeft}
        {Children}
        {IconRight}
      </button>
    </Tooltip>
  )
}

export const Button = forwardRef(ButtonWithRef)
